import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import type { Photo } from '../../lib/reglages';
import PhotoReglage from '../common/PhotoReglage';

const NewsletterSection = ({ photo }: { photo?: Photo | null }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError('Veuillez entrer une adresse email');
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Insert directly into the newsletter_subscribers table
      const { error: insertError } = await supabase
        .from('newsletter_subscribers')
        .insert([
          {
            email: email,
            source: 'website',
            active: true
          }
        ]);

      if (insertError) {
        // Check if it's a duplicate email error
        if (insertError.code === '23505') {
          setError('Cette adresse email est déjà inscrite à notre newsletter');
        } else {
          throw insertError;
        }
        return;
      }

      setSuccess('Vous êtes maintenant inscrit à notre newsletter !');
      setEmail(''); // Clear the input on success
    } catch (err) {
      console.error('Error subscribing to newsletter:', err);
      setError('Une erreur est survenue lors de l\'inscription');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="py-16 bg-primary-50">
      <div className="container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <div className="grid md:grid-cols-2">
            <div className="p-8 md:p-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Restez informé !
              </h2>
              <p className="text-gray-600 mb-6">
                Inscrivez-vous à notre newsletter pour être informé des nouveaux stages et offres spéciales.
              </p>
              
              {success ? (
                <div className="bg-green-50 p-4 rounded-lg flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-green-700">{success}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    placeholder="Votre adresse email"
                    className="form-input flex-grow"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  <button
                    type="submit"
                    className="btn-primary whitespace-nowrap"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <span className="flex items-center">
                        <Loader2 className="animate-spin mr-2 h-4 w-4" />
                        Inscription...
                      </span>
                    ) : (
                      "S'inscrire"
                    )}
                  </button>
                </form>
              )}
              
              {error && (
                <div className="mt-4 bg-red-50 p-3 rounded-lg flex items-start">
                  <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700">{error}</p>
                </div>
              )}
            </div>
            <div className="hidden md:block">
              <PhotoReglage photo={photo} className="h-full w-full" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsletterSection;